import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  uploadLab,
  confirmLabs,
  getLabPanels,
  getNotes,
  addNote,
  getMeds,
  addMed,
  updateMed,
  logMedIntake,
  type LabDraft,
  type LabResultDraft,
  type StoredLabPanel,
  type StoredNote,
  type StoredMed,
} from '../api.js';
import {
  emptyLabResult,
  updateResult,
  removeResult,
  canConfirm,
  buildConfirmBody,
  parseNumInput,
} from '../lib/labDraft.js';
import MarkerHistoryModal from '../components/MarkerHistoryModal.js';
import { latestMarkers, STATUS_META, type LabStatus } from '../lib/labStatus.js';
import { Button, Group, Icon, IconButton, IconTile, MoreRow, Page, Row, Sheet } from '../ui/index.js';

/** Prettify a canonical marker key (A1C, TOTAL_CHOLESTEROL) for display. */
function markerLabel(marker: string): string {
  return marker
    .split('_')
    .map((w) => (w.length <= 3 ? w : w.charAt(0) + w.slice(1).toLowerCase()))
    .join(' ');
}

/** Compact reference-range caption for a status card. */
function fmtRef(refLow: number | null, refHigh: number | null): string | null {
  if (refLow !== null && refHigh !== null) return `${refLow}–${refHigh}`;
  if (refHigh !== null) return `≤ ${refHigh}`;
  if (refLow !== null) return `≥ ${refLow}`;
  return null;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

const PILL: Record<LabStatus, string> = { good: 'good', amber: 'warn', red: 'bad', gray: '' };

function StatusPill({ status }: { status: LabStatus }): JSX.Element {
  const meta = STATUS_META[status];
  return (
    <span className={`hb-pill${PILL[status] ? ` hb-pill--${PILL[status]}` : ''}`} aria-label={meta.label}>
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

function ErrorLine({ msg }: { msg: string | null }): JSX.Element | null {
  if (!msg) return null;
  return (
    <p className="hb-form__error rec-error" role="alert">
      {msg}
    </p>
  );
}

// --- Labs ------------------------------------------------------------------

function LabsSection({ uploadRef }: { uploadRef: React.MutableRefObject<(() => void) | null> }): JSX.Element {
  const [panels, setPanels] = useState<StoredLabPanel[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState<LabDraft | null>(null);
  const [results, setResults] = useState<LabResultDraft[]>([]);
  const [panelDate, setPanelDate] = useState(today());
  const [provider, setProvider] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyMarker, setHistoryMarker] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [openPanel, setOpenPanel] = useState<StoredLabPanel | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  uploadRef.current = () => fileRef.current?.click();

  const reload = useCallback(() => {
    getLabPanels()
      .then(setPanels)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load labs.'))
      .finally(() => setLoaded(true));
  }, []);
  useEffect(() => reload(), [reload]);

  function onFile(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setBusy(true);
    uploadLab(file)
      .then((d) => {
        setDraft(d);
        setResults(d.results.length > 0 ? d.results : [emptyLabResult()]);
        if (d.panelDate) setPanelDate(d.panelDate);
        if (d.provider) setProvider(d.provider);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Upload failed.'))
      .finally(() => setBusy(false));
  }

  function confirm(): void {
    if (!canConfirm(panelDate, results)) return;
    setBusy(true);
    setError(null);
    confirmLabs(buildConfirmBody({ fileId: draft?.fileId, panelDate, provider, results }))
      .then(() => {
        setDraft(null);
        setResults([]);
        setProvider('');
        reload();
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Save failed.'))
      .finally(() => setBusy(false));
  }

  function cancel(): void {
    setDraft(null);
    setResults([]);
  }

  // Out-of-range first, then borderline — the ones worth a look lead.
  const RANK: Record<LabStatus, number> = { red: 0, amber: 1, good: 2, gray: 3 };
  const latest = latestMarkers(panels).sort((a, b) => RANK[a.status] - RANK[b.status]);
  const flagged = latest.filter((m) => m.status === 'red' || m.status === 'amber').length;
  const LIMIT = 8;
  const shown = showAll ? latest : latest.slice(0, LIMIT);

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
        hidden
        onChange={onFile}
      />
      <ErrorLine msg={error} />
      {busy && !draft && (
        <Group>
          <Row leading={<span className="rec-spin" aria-hidden="true" />} title="Reading your lab report…" sub="This takes a few seconds" />
        </Group>
      )}

      <Group
        header="Lab results"
        id="rec-labs"
        action={{ label: 'Upload', onClick: () => fileRef.current?.click() }}
        footer={flagged > 0 ? `${flagged} ${flagged === 1 ? 'marker needs' : 'markers need'} a look. Tap any marker for its history.` : 'Tap any marker for its history.'}
      >
        {shown.map((m) => {
          const ref = fmtRef(m.refLow, m.refHigh);
          return (
            <Row
              key={m.marker}
              className="rec-marker"
              title={markerLabel(m.marker)}
              sub={
                <span className="rec-marker__sub">
                  <StatusPill status={m.status} />
                  <span>{[ref ? `ref ${ref}` : null, fmtDate(m.date)].filter(Boolean).join(' · ')}</span>
                </span>
              }
              detail={
                <span className={`rec-value rec-value--${m.status}`}>
                  {m.value ?? m.textValue ?? '—'}
                  {m.unit && <span className="rec-value__unit"> {m.unit}</span>}
                </span>
              }
              onClick={() => setHistoryMarker(m.marker)}
              chevron
              ariaLabel={`${markerLabel(m.marker)} history`}
            />
          );
        })}
        {!showAll && latest.length > shown.length && (
          <MoreRow count={latest.length - shown.length} noun="more markers" onClick={() => setShowAll(true)} />
        )}
      </Group>

      {loaded && panels.length === 0 && !busy && (
        <Group header="Lab results" id="rec-labs-empty">
          <Row
            tone="accent"
            leading={<IconTile name="clipboard" color="#5e5ce6" />}
            title="Upload a lab report"
            sub="PDF or photo — markers are read for you, you confirm"
            onClick={() => fileRef.current?.click()}
          />
        </Group>
      )}

      <Group header="Panels" id="rec-panels">
        {panels.map((panel) => (
          <Row
            key={panel.id}
            leading={<IconTile name="clipboard" color="#5e5ce6" />}
            title={fmtDate(panel.panelDate)}
            sub={[
              panel.provider,
              panel.results
                .slice(0, 3)
                .map((r) => markerLabel(r.marker))
                .join(', ') + (panel.results.length > 3 ? ` +${panel.results.length - 3}` : ''),
            ]
              .filter(Boolean)
              .join(' · ')}
            detail={<span className="num">{panel.results.length}</span>}
            onClick={() => setOpenPanel(panel)}
          />
        ))}
      </Group>

      {draft && (
        <Sheet
          title="Confirm lab results"
          onClose={cancel}
          className="rec-sheet"
          action={{ label: busy ? 'Saving…' : 'Save', onClick: confirm, disabled: busy || !canConfirm(panelDate, results) }}
        >
          <div className="hb-form">
            {(draft.note || draft.extractionDisabled) && (
              <p className="hb-hint">{draft.note ?? 'AI extraction is off on the server. Enter results by hand.'}</p>
            )}
            <div className="hb-form__row">
              <label className="hb-field">
                <span>Date</span>
                <input type="date" value={panelDate} onChange={(e) => setPanelDate(e.target.value)} />
              </label>
              <label className="hb-field">
                <span>Provider</span>
                <input placeholder="e.g. Quest" value={provider} onChange={(e) => setProvider(e.target.value)} />
              </label>
            </div>

            <div className="rec-results" role="list" aria-label="Results">
              {results.map((r, i) => (
                <div className="rec-result" role="listitem" key={i}>
                  <div className="rec-result__top">
                    <input
                      className="rec-result__marker"
                      aria-label="Marker"
                      value={r.marker}
                      placeholder="Marker"
                      onChange={(e) => setResults((p) => updateResult(p, i, { marker: e.target.value }))}
                      title={r.rawName ?? ''}
                    />
                    <button
                      type="button"
                      className="rec-result__del"
                      aria-label="Remove"
                      onClick={() => setResults((p) => removeResult(p, i))}
                    >
                      <Icon name="trash" size={18} />
                    </button>
                  </div>
                  <div className="rec-result__nums">
                    <label className="hb-field">
                      <span>Value</span>
                      <input
                        inputMode="decimal"
                        value={r.value ?? ''}
                        onChange={(e) => setResults((p) => updateResult(p, i, { value: parseNumInput(e.target.value) }))}
                      />
                    </label>
                    <label className="hb-field">
                      <span>Unit</span>
                      <input
                        value={r.unit ?? ''}
                        onChange={(e) => setResults((p) => updateResult(p, i, { unit: e.target.value || null }))}
                      />
                    </label>
                    <label className="hb-field">
                      <span>Ref low</span>
                      <input
                        inputMode="decimal"
                        value={r.refLow ?? ''}
                        onChange={(e) => setResults((p) => updateResult(p, i, { refLow: parseNumInput(e.target.value) }))}
                      />
                    </label>
                    <label className="hb-field">
                      <span>Ref high</span>
                      <input
                        inputMode="decimal"
                        value={r.refHigh ?? ''}
                        onChange={(e) => setResults((p) => updateResult(p, i, { refHigh: parseNumInput(e.target.value) }))}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="secondary" icon="plus" onClick={() => setResults((p) => [...p, emptyLabResult()])}>
              Add row
            </Button>
            <ErrorLine msg={error} />
          </div>
        </Sheet>
      )}

      {openPanel && (
        <Sheet title={fmtDate(openPanel.panelDate)} onClose={() => setOpenPanel(null)}>
          {openPanel.provider && <p className="hb-hint rec-sheetsub">{openPanel.provider}</p>}
          <ul className="ui-group rec-menu">
            {openPanel.results.map((r) => (
              <Row
                key={r.id}
                title={markerLabel(r.marker)}
                detail={
                  <span className="num">
                    {r.value ?? r.textValue ?? '—'}
                    {r.unit ? ` ${r.unit}` : ''}
                  </span>
                }
                onClick={() => {
                  setOpenPanel(null);
                  setHistoryMarker(r.marker);
                }}
              />
            ))}
          </ul>
        </Sheet>
      )}

      {historyMarker && (
        <MarkerHistoryModal
          marker={historyMarker}
          label={markerLabel(historyMarker)}
          onClose={() => setHistoryMarker(null)}
        />
      )}
    </>
  );
}

// --- Notes -----------------------------------------------------------------

function NotesSection({ adding, setAdding }: { adding: boolean; setAdding: (v: boolean) => void }): JSX.Element {
  const [notes, setNotes] = useState<StoredNote[]>([]);
  const [text, setText] = useState('');
  const [provider, setProvider] = useState('');
  const [type, setType] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<StoredNote | null>(null);

  const reload = useCallback(() => {
    getNotes()
      .then(setNotes)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load notes.'));
  }, []);
  useEffect(() => reload(), [reload]);

  function save(): void {
    if (text.trim().length === 0 && !file) {
      setError('Add a note or an attachment.');
      return;
    }
    setBusy(true);
    setError(null);
    addNote({ text: text.trim() || undefined, provider: provider.trim() || undefined, type: type.trim() || undefined, file })
      .then(() => {
        setAdding(false);
        setText('');
        setProvider('');
        setType('');
        setFile(null);
        reload();
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Save failed.'))
      .finally(() => setBusy(false));
  }

  return (
    <>
      {!adding && <ErrorLine msg={error} />}
      <Group header="Doctor notes" id="rec-notes" action={{ label: 'Add', onClick: () => setAdding(true) }}>
        {notes.map((n) => (
          <Row
            key={n.id}
            leading={<IconTile name="file" color="#0a84ff" />}
            title={[n.provider, n.type].filter(Boolean).join(' · ') || 'Note'}
            sub={n.text ? <span className="rec-clamp">{n.text}</span> : n.attachments.length > 0 ? 'Attachment' : undefined}
            detail={fmtDate(n.noteDate)}
            onClick={() => setOpen(n)}
          />
        ))}
      </Group>
      {notes.length === 0 && (
        <Group header="Doctor notes" id="rec-notes-empty">
          <Row
            tone="accent"
            leading={<IconTile name="file" color="#0a84ff" />}
            title="Add a doctor note"
            sub="Visit notes, imaging, attachments"
            onClick={() => setAdding(true)}
          />
        </Group>
      )}

      {adding && (
        <Sheet
          title="Doctor note"
          onClose={() => setAdding(false)}
          action={{ label: busy ? 'Saving…' : 'Save', onClick: save, disabled: busy }}
        >
          <div className="hb-form">
            <div className="hb-form__row">
              <label className="hb-field">
                <span>Provider</span>
                <input placeholder="Dr. Patel" value={provider} onChange={(e) => setProvider(e.target.value)} />
              </label>
              <label className="hb-field">
                <span>Type</span>
                <input placeholder="Visit, imaging…" value={type} onChange={(e) => setType(e.target.value)} />
              </label>
            </div>
            <label className="hb-field">
              <span>Note</span>
              <textarea placeholder="What did they say?" rows={4} value={text} onChange={(e) => setText(e.target.value)} />
            </label>
            <label className="ui-btn ui-btn--secondary ui-btn--md rec-attach">
              <Icon name="file" size={18} />
              {file ? file.name : 'Attach file'}
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <ErrorLine msg={error} />
          </div>
        </Sheet>
      )}

      {open && (
        <Sheet title={[open.provider, open.type].filter(Boolean).join(' · ') || 'Note'} onClose={() => setOpen(null)}>
          <p className="hb-hint">{fmtDate(open.noteDate)}</p>
          {open.text && <p className="rec-notetext">{open.text}</p>}
          {open.attachments.length > 0 && (
            <div className="rec-files">
              {open.attachments.map((id, i) => (
                <a key={id} className="ui-btn ui-btn--secondary ui-btn--md" href={`/api/files/${id}`} target="_blank" rel="noreferrer">
                  <Icon name="file" size={18} />
                  Attachment{open.attachments.length > 1 ? ` ${i + 1}` : ''}
                </a>
              ))}
            </div>
          )}
        </Sheet>
      )}
    </>
  );
}

// --- Meds ------------------------------------------------------------------

function lastTaken(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `Taken today ${time}` : `Last ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

function MedsSection({
  adding,
  setAdding,
  part,
}: {
  adding: boolean;
  setAdding: (v: boolean) => void;
  part: 'active' | 'inactive';
}): JSX.Element | null {
  const [meds, setMeds] = useState<StoredMed[]>([]);
  const [name, setName] = useState('');
  const [dose, setDose] = useState('');
  const [schedule, setSchedule] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openMed, setOpenMed] = useState<StoredMed | null>(null);

  const reload = useCallback(() => {
    getMeds()
      .then(setMeds)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load meds.'));
  }, []);
  useEffect(() => reload(), [reload]);
  // The two parts are separate instances; refresh when the other one changes.
  useEffect(() => {
    const on = (): void => reload();
    window.addEventListener('records:meds', on);
    return () => window.removeEventListener('records:meds', on);
  }, [reload]);
  const changed = (): void => {
    window.dispatchEvent(new Event('records:meds'));
  };

  function save(): void {
    if (name.trim().length === 0) return;
    setBusy(true);
    setError(null);
    addMed({ name: name.trim(), dose: dose.trim() || null, schedule: schedule.trim() || null, active: true })
      .then(() => {
        setAdding(false);
        setName('');
        setDose('');
        setSchedule('');
        changed();
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Save failed.'))
      .finally(() => setBusy(false));
  }

  function taken(id: string): void {
    logMedIntake(id).then(changed).catch(() => setError('Could not log intake.'));
  }

  function toggleActive(m: StoredMed): void {
    updateMed(m.id, { active: !m.active }).then(changed).catch(() => setError('Update failed.'));
  }

  const active = meds.filter((m) => m.active);
  const inactive = meds.filter((m) => !m.active);
  const meta = (m: StoredMed): string =>
    [m.dose, typeof m.schedule === 'string' ? m.schedule : null].filter(Boolean).join(' · ');

  if (part === 'inactive') {
    return (
      <>
      {active.length === 0 && (
        <Group header="Medications" id="rec-meds-empty">
          <Row
            tone="accent"
            leading={<IconTile name="plus" color="#ff9f0a" />}
            title="Track a medication"
            sub="One tap to log each dose"
            onClick={() => setAdding(true)}
          />
        </Group>
      )}
      <Group header="Stopped medications" id="rec-meds-off">
        {inactive.map((m) => (
          <Row
            key={m.id}
            tone="muted"
            title={m.name}
            sub={meta(m) || undefined}
            trailing={
              <Button variant="secondary" size="sm" onClick={() => toggleActive(m)}>
                Resume
              </Button>
            }
          />
        ))}
      </Group>
      </>
    );
  }

  return (
    <>
      {!adding && <ErrorLine msg={error} />}
      <Group
        header="Medications"
        id="rec-meds"
        action={{ label: 'Add', onClick: () => setAdding(true) }}
      >
        {active.map((m) => {
          const takenToday = m.lastTakenAt ? new Date(m.lastTakenAt).toDateString() === new Date().toDateString() : false;
          return (
            <Row
              key={m.id}
              leading={<IconTile name={takenToday ? 'check' : 'clock'} color={takenToday ? '#30d158' : '#ff9f0a'} />}
              title={m.name}
              sub={[meta(m), m.lastTakenAt ? lastTaken(m.lastTakenAt) : 'Not logged yet'].filter(Boolean).join(' · ')}
              swipe={{ trailing: [{ label: 'Stop', tone: 'neutral', onAction: () => toggleActive(m) }] }}
              onClick={() => setOpenMed(m)}
              chevron={false}
              trailing={
                <>
                  <Button variant={takenToday ? 'secondary' : 'primary'} size="sm" onClick={() => taken(m.id)}>
                    {takenToday ? 'Again' : 'Taken'}
                  </Button>
                </>
              }
            />
          );
        })}
      </Group>

      {openMed && (
        <Sheet title={openMed.name} onClose={() => setOpenMed(null)}>
          <div className="hb-form">
            <p className="hb-hint">
              {[meta(openMed), openMed.lastTakenAt ? lastTaken(openMed.lastTakenAt) : 'Not logged yet'].filter(Boolean).join(' · ')}
            </p>
            <Button
              size="lg"
              block
              icon="check"
              onClick={() => {
                taken(openMed.id);
                setOpenMed(null);
              }}
            >
              Mark taken now
            </Button>
            <Button
              variant="destructive"
              size="lg"
              block
              onClick={() => {
                toggleActive(openMed);
                setOpenMed(null);
              }}
            >
              Stop taking
            </Button>
          </div>
        </Sheet>
      )}

      {adding && (
        <Sheet
          title="Medication"
          onClose={() => setAdding(false)}
          action={{ label: busy ? 'Saving…' : 'Save', onClick: save, disabled: busy || name.trim().length === 0 }}
        >
          <div className="hb-form">
            <label className="hb-field">
              <span>Name</span>
              <input placeholder="Vitamin D" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </label>
            <div className="hb-form__row">
              <label className="hb-field">
                <span>Dose</span>
                <input placeholder="10 mg" value={dose} onChange={(e) => setDose(e.target.value)} />
              </label>
              <label className="hb-field">
                <span>Schedule</span>
                <input placeholder="Daily" value={schedule} onChange={(e) => setSchedule(e.target.value)} />
              </label>
            </div>
            <ErrorLine msg={error} />
          </div>
        </Sheet>
      )}
    </>
  );
}

export default function Records(): JSX.Element {
  const upload = useRef<(() => void) | null>(null);
  const [menu, setMenu] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [addingMed, setAddingMed] = useState(false);

  const pick = (fn: () => void): void => {
    setMenu(false);
    fn();
  };

  return (
    <Page
      title="Records"
      sub="Labs, doctor notes and medications"
      actions={<IconButton icon="plus" label="Add a record" onClick={() => setMenu(true)} />}
      className="rec"
    >
      <MedsSection part="active" adding={addingMed} setAdding={setAddingMed} />
      <LabsSection uploadRef={upload} />
      <NotesSection adding={addingNote} setAdding={setAddingNote} />
      <MedsSection part="inactive" adding={false} setAdding={setAddingMed} />

      {menu && (
        <Sheet title="Add a record" onClose={() => setMenu(false)}>
          <ul className="ui-group rec-menu">
            <Row
              leading={<IconTile name="clipboard" color="#5e5ce6" />}
              title="Lab report"
              sub="PDF or photo — markers are read for you"
              onClick={() => pick(() => upload.current?.())}
            />
            <Row
              leading={<IconTile name="file" color="#0a84ff" />}
              title="Doctor note"
              sub="Visit notes, imaging, attachments"
              onClick={() => pick(() => setAddingNote(true))}
            />
            <Row
              leading={<IconTile name="plus" color="#ff9f0a" />}
              title="Medication"
              sub="Name, dose and schedule"
              onClick={() => pick(() => setAddingMed(true))}
            />
          </ul>
        </Sheet>
      )}
    </Page>
  );
}
